import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { API } from '@/api'
import { ServerInstanceInfo } from '@/api/type-server'
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { Button, Divider, message, Modal, Trans } from '@/components'
import { TeamRole } from '@/consts/team'
import { useDomain } from '@/contexts/DomainContext'
import { Instance } from '@/models/instance'
import { useTeamStore, useTeamUserRole } from '@/stores/team'
import { formatDomain, formatPrice } from '@/utils/formatter'

import { InstanceDetailContent } from '../components/InstanceDetailContent'

export function AdminInstanceDetailPage() {
  const { t } = useTranslation()
  const { instanceId } = useParams<{ instanceId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const userRole = useTeamUserRole()
  const teamStore = useTeamStore()
  const { domainInfos } = useDomain()

  const instanceData = location.state?.instanceData as ServerInstanceInfo | undefined
  const memberData = location.state?.memberData

  // Create Instance object from server data
  const instance = instanceData ? new Instance(instanceData) : null

  // Redirect if no data
  useEffect(() => {
    if (!instance) {
      navigate('/team/instances/admin')
    }
  }, [instance, navigate])

  // Don't render if no data
  if (!instance) {
    return null
  }

  // Format data for InstanceDetailContent
  const id = instance.resID
  const name = instance.displayTitle
  const region =
    domainInfos.find((info) => info.domain === instance.domain)?.displayName ||
    formatDomain(instance.domain)
  const status = instance.statusText
  const statusStyle = instance.statusTextStyle
  const cost = String(formatPrice(instance.cost, false))
  const ownerName = memberData?.user?.nickname || memberData?.user?.name || 'Unknown'

  // Handle copy to clipboard
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    message.success(t('common.copied', 'Copied to clipboard'))
  }

  // Check if user can manage (Admin or Owner)
  const canManage = userRole === TeamRole.Admin || userRole === TeamRole.Owner

  // Handle release
  const handleRelease = async () => {
    const teamResID = teamStore.teamTokenData?.teamResID
    if (!teamResID) return

    try {
      await Modal.confirm({
        title: t('instance.releaseModal.title'),
        content: (
          <div className="bg-panel-gray-100 rounded-lg p-4 flex-row mb-4">
            <div>
              <div className="text-[20px] leading-7 font-bold text-primary">{name}</div>
            </div>
            <Divider className="my-3" />
            <div>
              <div className="t-sb text-secondary font-medium">
                <Trans i18nKey="instance.releaseModal.content">
                  The machine will be stopped and released, please save the environment if you need
                  it.
                </Trans>
              </div>
              <div className="text-btn-secondary t-sb cursor-pointer">
                <Trans i18nKey="instance.releaseModal.whySaveEnvironment">
                  Why save the environment?
                </Trans>
              </div>
            </div>
          </div>
        ),
        confirmBtnCfg: {
          text: t('instance.releaseModal.stopReleaseBtn'),
          type: 'danger',
        },
        className: 'w-[800px]',
      })

      const res = await API.deleteTeamMemberInstance({
        res_id: teamResID,
        inst_res_id: instance.resID,
      })

      if (res.code === 0) {
        message.success(t('common.message.success'))
        navigate('/team/instances/admin')
      }
    } catch (error) {
      console.error('Failed to release instance:', error)
    }
  }

  return (
    <div className="card bg-panel-white p-8 flex flex-col gap-8 h-full overflow-y-auto">
      {/* Section 1: Breadcrumb Navigation */}
      <div className="border-b border-neutral-200 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/team/instances/admin" className="p-1 bg-panel-gray-100 rounded-md">
              <ArrowLeftIcon className="w-6 h-6 text-tertiary" />
            </Link>
            <div className="h6">
              <span className="text-tertiary">
                <Trans i18nKey="instance.breadcrumb.instances">Instances</Trans>
              </span>
              <span> / </span>
              <span className="text-primary">{instanceId}</span>
            </div>
          </div>

          {/* Action Button - Only for Admin/Owner */}
          {canManage && instance.canRelease && (
            <div className="flex gap-3">
              <Button type="warning" size="md" onClick={handleRelease} className="min-w-[88px]">
                <Trans i18nKey="instance.list.action.release">Release</Trans>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Instance Detail Content: Basic Information, Owner, Access, Config, Hardware */}
      <InstanceDetailContent
        id={id}
        name={name}
        region={region}
        status={{
          label: status,
          style: statusStyle,
        }}
        cost={cost}
        ownerName={ownerName}
        instance={instance}
        teamResID={teamStore.teamTokenData?.teamResID || ''}
        memberResID={memberData?.resID || ''}
        onCopyId={handleCopy}
      />
    </div>
  )
}
